// Growem — UI state: active tab, meal sheet, and the count-up rest timer. The timer counts UP
// against wall-clock time (persisted so it survives reload/backgrounding); the athlete stops it
// with "Listo", which records the actual rest seconds for that set.

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { TabId } from '../data/plan'
import { zoneOf, type RestBand } from '../domain/rest'
import { soundStart, soundMin, soundIdeal, soundMax, soundDone } from '../lib/sound'

const REST_KEY = 'fierro_rest'
type Zone = 'under' | 'ideal' | 'over'

interface RestState {
  active: boolean
  elapsed: number                 // counts UP (seconds)
  min: number; ideal: number; max: number
  day: string; setKey: string     // where to record the rest
  done: null | { elapsed: number; zone: Zone } // brief freeze/confirm after Listo
  pulse: boolean                  // transient: min-crossing "you can go" ring
  minimized: boolean              // collapsed to a floating pill so the app stays navigable
}

interface UIApi {
  tab: TabId
  setTab: (t: TabId) => void
  rest: RestState
  startRest: (band: RestBand, day: string, setKey: string) => void
  adjustRest: (deltaS: number) => void
  stopRest: () => { day: string; setKey: string; elapsed: number } | null
  discardRest: () => void
  minimizeRest: () => void
  expandRest: () => void
  // meal sheet (global overlay; opened from Bitácora and the #comida deep-link)
  mealOpen: boolean
  mealPrefill: string
  openMeal: (prefill?: string) => void
  closeMeal: () => void
}

const Ctx = createContext<UIApi | null>(null)
export function useUI(): UIApi {
  const v = useContext(Ctx)
  if (!v) throw new Error('useUI must be used within <UIProvider>')
  return v
}

interface Saved { start: number; min: number; ideal: number; max: number; day: string; setKey: string }
function readRest(): Saved | null {
  try { return JSON.parse(localStorage.getItem(REST_KEY) || 'null') } catch { return null }
}

const REST0: RestState = { active: false, elapsed: 0, min: 0, ideal: 0, max: 1, day: '', setKey: '', done: null, pulse: false, minimized: false }

export function UIProvider({ children }: { children: ReactNode }) {
  const [tab, setTab] = useState<TabId>('resumen')
  const [rest, setRest] = useState<RestState>(REST0)
  const [mealOpen, setMealOpen] = useState(false)
  const [mealPrefill, setMealPrefill] = useState('')
  const openMeal = useCallback((prefill = '') => { setMealPrefill(prefill); setMealOpen(true) }, [])
  const closeMeal = useCallback(() => setMealOpen(false), [])

  const timer = useRef<number | null>(null)
  const startRef = useRef(0)
  const bandRef = useRef<RestBand>({ min: 0, ideal: 0, max: 1 })
  const crossedMin = useRef(false)
  const crossedIdeal = useRef(false)
  const crossedMax = useRef(false)
  const restRef = useRef(rest); restRef.current = rest

  const clear = useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null }
  }, [])

  const tick = useCallback(() => {
    const b = bandRef.current
    const elapsed = Math.max(0, Math.floor((Date.now() - startRef.current) / 1000))
    setRest((r) => (r.active ? { ...r, elapsed } : r))
    if (!crossedMin.current && elapsed >= b.min) {
      crossedMin.current = true
      try { navigator.vibrate?.([30, 40, 30]) } catch { /* no vibrate */ }
      soundMin()
      setRest((r) => ({ ...r, pulse: true }))
      window.setTimeout(() => setRest((r) => ({ ...r, pulse: false })), 650)
    }
    if (!crossedIdeal.current && b.ideal > b.min && elapsed >= b.ideal) {
      crossedIdeal.current = true
      try { navigator.vibrate?.(20) } catch { /* no vibrate */ }
      soundIdeal()
    }
    if (!crossedMax.current && elapsed > b.max) {
      crossedMax.current = true
      try { navigator.vibrate?.(18) } catch { /* no vibrate */ }
      soundMax()
    }
  }, [])

  const run = useCallback(() => {
    clear()
    timer.current = window.setInterval(tick, 250)
    tick()
  }, [clear, tick])

  const startRest = useCallback((band: RestBand, day: string, setKey: string) => {
    startRef.current = Date.now()
    bandRef.current = band
    crossedMin.current = false
    crossedIdeal.current = false
    crossedMax.current = false
    localStorage.setItem(REST_KEY, JSON.stringify({ start: startRef.current, ...band, day, setKey }))
    setRest({ active: true, elapsed: 0, min: band.min, ideal: band.ideal, max: band.max, day, setKey, done: null, pulse: false, minimized: false })
    soundStart()
    run()
  }, [run])

  // Correct the running counter (e.g. you started resting before hitting the set): shift the
  // wall-clock start. +delta adds elapsed time, -delta removes it (clamped so elapsed >= 0).
  // Persisted too, so the correction survives reload. Threshold sounds fire naturally on the next
  // tick when adding; already-crossed flags stay crossed when subtracting (no repeat beeps).
  const adjustRest = useCallback((deltaS: number) => {
    if (!restRef.current.active) return
    const newStart = Math.min(Date.now(), startRef.current - deltaS * 1000)
    startRef.current = newStart
    const saved = readRest()
    if (saved) localStorage.setItem(REST_KEY, JSON.stringify({ ...saved, start: newStart }))
    tick()
  }, [tick])

  const minimizeRest = useCallback(() => setRest((s) => ({ ...s, minimized: true })), [])
  const expandRest = useCallback(() => setRest((s) => ({ ...s, minimized: false })), [])

  const stopRest = useCallback((): { day: string; setKey: string; elapsed: number } | null => {
    const r = restRef.current
    if (!r.active) return null
    clear()
    soundDone()
    const elapsed = Math.max(0, Math.floor((Date.now() - startRef.current) / 1000))
    const zone = zoneOf(elapsed, bandRef.current)
    setRest((s) => ({ ...s, active: false, elapsed, done: { elapsed, zone }, pulse: false }))
    localStorage.removeItem(REST_KEY)
    window.setTimeout(() => setRest((s) => ({ ...s, done: null })), 1200)
    return { day: r.day, setKey: r.setKey, elapsed }
  }, [clear])

  const discardRest = useCallback(() => {
    clear()
    localStorage.removeItem(REST_KEY)
    setRest((s) => ({ ...s, active: false, done: null, pulse: false }))
  }, [clear])

  // Resume an in-flight rest timer on mount (wall-clock survives reload/backgrounding).
  useEffect(() => {
    const saved = readRest()
    if (saved) {
      const elapsed = Math.floor((Date.now() - saved.start) / 1000)
      if (elapsed > 1800) {
        localStorage.removeItem(REST_KEY) // stale-resume guard: abandoned rest
      } else {
        startRef.current = saved.start
        bandRef.current = { min: saved.min, ideal: saved.ideal, max: saved.max }
        crossedMin.current = elapsed >= saved.min
        crossedIdeal.current = elapsed >= saved.ideal
        crossedMax.current = elapsed > saved.max
        setRest({ active: true, elapsed: Math.max(0, elapsed), min: saved.min, ideal: saved.ideal, max: saved.max, day: saved.day, setKey: saved.setKey, done: null, pulse: false, minimized: false })
        run()
      }
    }
    return clear
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Ctx.Provider value={{ tab, setTab, rest, startRest, adjustRest, stopRest, discardRest, minimizeRest, expandRest, mealOpen, mealPrefill, openMeal, closeMeal }}>
      {children}
    </Ctx.Provider>
  )
}
